/**
 * launchCampaign — RPC contract for the callable that commits the money.
 * `campaign.entity.ts` marks `launch` `.requiresHandler('launchCampaign')`, so
 * no client write reaches `building` and this call is the only path there.
 *
 * The input names the campaign and carries NO part of the document. The
 * predicate the callable re-checks reads the campaign's own state, and a
 * caller-supplied body would let a caller describe a campaign that flatters
 * the check — so the shape simply cannot express one, and the callable reads
 * the document itself inside the transaction that moves it. `orgId` is
 *
 * Fields justified:
 *   eventId    — the event whose campaign this is; the path is
 *                workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}.
 *   campaignId — the campaign to commit.
 * Excluded:
 *   budgetMinor, proposal, priceFloorCents, proposedAt — every one of them is
 *           already on the document, and a copy travelling beside the call is
 *           free to disagree with the record the transaction actually reads.
 *   a confirmation flag — the call IS the confirmation; a flag the caller sets
 *           to true guards nothing the caller could not set anyway.
 *
 * Output: a discriminated union on `outcome`, because a refusal is the answer
 * the operator asked for — a proposal composed against a world that has since
 * moved — and not an exception. An unpermitted caller still throws
 * `HttpsError`; that is a closed door rather than an answer.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const LaunchCampaignInput = z
	.object({
		orgId: Id,
		eventId: Id.describe("the event this campaign promotes"),
		campaignId: Id.describe("the campaign to commit the budget for"),
	})
	.strict();
export type LaunchCampaignInput = z.infer<typeof LaunchCampaignInput>;

// The two ways a parked proposal stops describing the world it was composed
// against: the cheapest publicly purchasable ticket has moved, so the ad's own
// from-price is already wrong, or it has simply sat long enough that the
// catalogue underneath it may have.
export const LaunchRefusalSchema = z
	.enum(["priceFloorMoved", "proposalStale"])
	.describe(
		"why the confirmation was refused — a claim the proposal makes that the event no longer supports",
	);
export type LaunchRefusal = z.infer<typeof LaunchRefusalSchema>;

export const LaunchCampaignOutput = z.discriminatedUnion("outcome", [
	z
		.object({
			outcome: z.literal("building"),
			priceFloorCents: z
				.number()
				.int()
				.min(0)
				.describe(
					"the floor the copy was composed against, re-read at the confirmation and unchanged — the figure the ad will actually carry",
				),
		})
		.strict(),
	z
		.object({
			outcome: z.literal("refused"),
			reason: LaunchRefusalSchema,
		})
		.strict(),
]);
export type LaunchCampaignOutput = z.infer<typeof LaunchCampaignOutput>;
